import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import useMedia from 'use-media'

import {
  Box,
  MotionBox,
  Text,
  HiddenInput,
  mediaQueries,
} from '@redriverpress/system'

const ToggleControlOuter = styled(Box).attrs(props => ({
  bg: props.active ? 'blue700' : 'gray700',
  transition: 'background 0.125s linear',
  display: 'flex',
  alignItems: 'center',
  height: '2rem',
  width: props.larger ? '5rem' : ['4rem', null, null, '5rem'],
  borderRadius: '1rem',
  position: 'relative',
  px: 2,
  flexShrink: 0,
}))`
  user-select: none;
`

const ToggleControlIndicator = styled(MotionBox).attrs(props => ({
  size: '1.25rem',
  borderRadius: 'circular',
  bg: 'white',
  position: 'absolute',
  animate: {
    x: props.active ? props.translateBy : 0,
  },
  initial: false,
  transition: {
    type: 'spring',
    stiffness: 500,
    damping: 40,
    mass: 0.5,
  },
}))``

const ToggleControlStatus = styled(Text).attrs({
  color: 'white',
  fontWeight: '600',
  fontSize: ['12px', 0],
  m: 0,
})`
  text-transform: uppercase;
  transition: opacity 0.125s linear;
`

const Label = styled(Text).attrs({
  as: 'span',
  display: 'inline-block',
  fontSize: [0, null, null, 1],
  fontWeight: 600,
  id: 'toggleLabel',
})`
  ${({ hideLabel }) =>
    hideLabel &&
    css`
      position: absolute;
      height: 1px;
      width: 1px;
      overflow: hidden;
      clip: rect(1px, 1px, 1px, 1px);
    `}
`
export default function Toggle({
  label,
  hideLabel,
  active,
  onChange,
  larger,
  labelLeft,
  showStatusLabel,
  ...props
}) {
  const isLg = useMedia(mediaQueries.lg)

  return (
    <Text
      as='label'
      cursor='pointer'
      display='flex'
      alignItems='center'
      {...props}
    >
      <HiddenInput
        type='checkbox'
        checked={active}
        onChange={onChange}
        role='switch'
        aria-labelledby='toggleLabel'
        aria-checked={active}
      />
      {labelLeft && (
        <Label pr={2} hideLabel={hideLabel}>
          {label}
        </Label>
      )}
      <ToggleControlOuter active={active} role='presentation' larger={larger}>
        <ToggleControlIndicator
          active={active}
          translateBy={isLg || larger ? '2.75rem' : '1.75rem'}
        />
        {showStatusLabel && (
          <Box
            display='flex'
            px={[0, null, null, 2]}
            justifyContent='space-evenly'
            width={1}
          >
            <ToggleControlStatus opacity={active ? 1 : 0}>
              On
            </ToggleControlStatus>
            <ToggleControlStatus opacity={active ? 0 : 1}>
              Off
            </ToggleControlStatus>
          </Box>
        )}
      </ToggleControlOuter>
      {!labelLeft && (
        <Label pl={2} hideLabel={hideLabel}>
          {label}
        </Label>
      )}
    </Text>
  )
}

Toggle.defaultProps = {
  hideLabel: false,
  larger: false,
  labelLeft: false,
  showStatusLabel: true,
}

Toggle.propTypes = {
  active: PropTypes.bool.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  hideLabel: PropTypes.bool,
  larger: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  labelLeft: PropTypes.bool,
  showStatusLabel: PropTypes.bool,
}
